import { useEffect, useState } from 'react';
import { type PaintMatchResponseItem } from '@pomoshnik/shared';

/**
 * Список карточек-результатов после photo-match для paint (Block 3 Phase 4 Шаг 6).
 * Презентационный компонент — backend возвращает уже отсортированные по
 * deltaE76 (asc) карточки с явной меткой «ΔE = N».
 *
 * Differences vs decor MatchResults:
 *   - Item type — PaintMatchResponseItem (no `kind`, has `ralCode`,
 *     `primaryPhotoKey` с `.webp` именем).
 *   - Реальный <img src="/paint-photos/..."> через `encodeURI` + onError fallback
 *     на инициал бренда (свeтлый square с буквой). ADR-0011 dev storage:
 *     paint-photos/ commit'нуты в `apps/web/public/`.
 *   - RAL badge заметнее brand'а (для краски RAL — главный идентификатор).
 *
 * deltaE отображается прямо на карточке — основная сигнальная метрика.
 * Округление до 1 десятичного знака как у decor MatchResults.
 */
export interface MatchResultsProps {
  items: readonly PaintMatchResponseItem[];
  onItemClick: (globalId: string) => void;
}

export function MatchResults({ items, onItemClick }: MatchResultsProps) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted text-base m-0">Найдено похожих: {items.length}</p>
      <div className="flex flex-col gap-4">
        {items.map((item) => (
          <PaintMatchCard
            key={item.globalId}
            item={item}
            onClick={() => onItemClick(item.globalId)}
          />
        ))}
      </div>
    </div>
  );
}

function PaintMatchCard({ item, onClick }: { item: PaintMatchResponseItem; onClick: () => void }) {
  const [imageFailed, setImageFailed] = useState(false);
  // Сброс fail-state при смене photo key (symmetry с PaintResultCard:40-42).
  // results-state статичен между match'ами, но defensive: если parent
  // переиспользует React-position для другого item — sticky imageFailed
  // не должен скрывать валидное новое фото.
  useEffect(() => {
    setImageFailed(false);
  }, [item.primaryPhotoKey]);
  const showImage = !!item.primaryPhotoKey && !imageFailed;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Открыть карточку: ${item.name}, ${item.ralCode}`}
      className={[
        'w-full rounded-xl bg-card border-[1.5px] border-accent shadow-card overflow-hidden',
        'text-left p-0 cursor-pointer flex',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      {/* Square photo area — left side */}
      <div className="w-24 h-24 bg-icon-bg flex items-center justify-center shrink-0 overflow-hidden">
        {showImage ? (
          <img
            // ADR-0011: encodeURI (не encodeURIComponent) — кириллица + пробелы.
            src={`/paint-photos/${encodeURI(item.primaryPhotoKey ?? '')}`}
            alt={`Краска ${item.name}, ${item.ralCode}`}
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-muted text-2xl font-display" aria-hidden="true">
            {item.brand.charAt(0)}
          </span>
        )}
      </div>
      {/* Content area — right side */}
      <div className="p-3 flex-1 min-w-0 flex flex-col gap-1">
        <p className="font-body text-[16px] font-semibold text-ink m-0 line-clamp-2">{item.name}</p>
        <p className="text-muted text-sm m-0">
          <span className="font-medium text-ink">{item.ralCode}</span> · {item.brand}
        </p>
        <p className="text-separator text-sm m-0 mt-auto">ΔE = {item.deltaE.toFixed(1)}</p>
      </div>
    </button>
  );
}
